/**
 * Compaction Helpers
 *
 * Utilities for full compaction (LLM-based summarization).
 * Used by the reference compaction implementation and available
 * for custom CompactFunction implementations.
 */

import type { CompactContext, SessionMessage } from "../session/types";
import { estimateMessageTokens } from "./tokens";

export type CompactTokenCounter = (
  messages: SessionMessage[]
) => number | Promise<number>;

// ── Compaction ID constants ─────────────────────────────────────────

/** Prefix for all compaction messages (overlays and summaries) */
export const COMPACTION_PREFIX = "compaction_";

/** Check if a message is a compaction message */
export function isCompactionMessage(msg: SessionMessage): boolean {
  return msg.id.startsWith(COMPACTION_PREFIX);
}

// ── Tool Pair Alignment ──────────────────────────────────────────────

/**
 * Check if a message contains tool invocations.
 */
function hasToolCalls(msg: SessionMessage): boolean {
  return msg.parts.some(
    (p) => p.type.startsWith("tool-") || p.type === "dynamic-tool"
  );
}

/**
 * Get tool call IDs from a message's parts.
 */
function getToolCallIds(msg: SessionMessage): Set<string> {
  const ids = new Set<string>();
  for (const part of msg.parts) {
    if (
      (part.type.startsWith("tool-") || part.type === "dynamic-tool") &&
      "toolCallId" in part
    ) {
      ids.add((part as { toolCallId: string }).toolCallId);
    }
  }
  return ids;
}

/**
 * Check if a message is a tool result referencing a specific call ID.
 */
function isToolResultFor(msg: SessionMessage, callIds: Set<string>): boolean {
  return msg.parts.some(
    (p) =>
      (p.type.startsWith("tool-") || p.type === "dynamic-tool") &&
      "toolCallId" in p &&
      callIds.has((p as { toolCallId: string }).toolCallId)
  );
}

/**
 * Align a boundary index forward to avoid splitting tool call/result groups.
 * If the boundary falls between an assistant message with tool calls and its
 * tool results, move it forward past the results.
 */
export function alignBoundaryForward(
  messages: SessionMessage[],
  idx: number
): number {
  if (idx <= 0 || idx >= messages.length) return idx;

  // Check if the message before the boundary has tool calls
  const prev = messages[idx - 1];
  if (prev.role === "assistant" && hasToolCalls(prev)) {
    const callIds = getToolCallIds(prev);
    // Skip forward past any tool results for these calls
    while (idx < messages.length && isToolResultFor(messages[idx], callIds)) {
      idx++;
    }
  }

  return idx;
}

/**
 * Align a boundary index backward to avoid splitting tool call/result groups.
 * If the boundary falls in the middle of tool results, move it backward to
 * include the assistant message that made the calls.
 */
export function alignBoundaryBackward(
  messages: SessionMessage[],
  idx: number
): number {
  if (idx <= 0 || idx >= messages.length) return idx;

  // If the message at idx is a tool result, walk backward to find the call
  while (idx > 0) {
    const msg = messages[idx];
    if (msg.role === "assistant" && hasToolCalls(msg)) {
      break; // This is a tool call message — include it
    }
    // Check if this looks like a tool result (assistant message following another)
    const prev = messages[idx - 1];
    if (prev.role === "assistant" && hasToolCalls(prev)) {
      const callIds = getToolCallIds(prev);
      if (isToolResultFor(msg, callIds)) {
        idx--; // Move back to include the call
        continue;
      }
    }
    break;
  }

  return idx;
}

// ── Token-Budget Tail Protection ─────────────────────────────────────

/**
 * Find the compression end boundary using a token budget for the tail.
 * Walks backward from the end, accumulating tokens until budget is reached.
 * Returns the index where compression should stop (everything from this
 * index onward is protected).
 *
 * @param messages All messages
 * @param headEnd Index where the protected head ends (compression starts here)
 * @param tailTokenBudget Maximum tokens to keep in the tail
 * @param minTailMessages Minimum messages to protect in the tail (fallback)
 */
export function findTailCutByTokens(
  messages: SessionMessage[],
  headEnd: number,
  tailTokenBudget = 20000,
  minTailMessages = 2
): number {
  const n = messages.length;
  let accumulated = 0;
  let tokenCut = n;

  for (let i = n - 1; i >= headEnd; i--) {
    const msgTokens = estimateMessageTokens([messages[i]]);

    if (accumulated + msgTokens > tailTokenBudget && tokenCut < n) {
      // Budget exceeded and we already have at least one tail message
      break;
    }
    accumulated += msgTokens;
    tokenCut = i;
  }

  // Protect whichever is larger: token-based tail or minTailMessages
  const minCut = n - minTailMessages;
  const cutIdx = minCut >= headEnd ? Math.min(tokenCut, minCut) : tokenCut;

  // Align to avoid splitting tool groups
  return alignBoundaryBackward(messages, cutIdx);
}

async function findTailCutByTokensWithCounter(
  messages: SessionMessage[],
  headEnd: number,
  tokenCounter: CompactTokenCounter,
  tailTokenBudget = 20000,
  minTailMessages = 2
): Promise<number> {
  const n = messages.length;
  let accumulated = 0;
  let tokenCut = n;

  for (let i = n - 1; i >= headEnd; i--) {
    const msgTokens = await tokenCounter([messages[i]]);

    if (accumulated + msgTokens > tailTokenBudget && tokenCut < n) {
      break;
    }
    accumulated += msgTokens;
    tokenCut = i;
  }

  const minCut = n - minTailMessages;
  const cutIdx = minCut >= headEnd ? Math.min(tokenCut, minCut) : tokenCut;
  return alignBoundaryBackward(messages, cutIdx);
}

// ── Tool Pair Sanitization ───────────────────────────────────────────

/**
 * Fix orphaned tool call/result pairs after compaction.
 *
 * Two failure modes:
 * 1. Tool result references a call_id whose assistant tool_call was removed
 *    → Remove the orphaned result
 * 2. Assistant has tool_calls whose results were dropped
 *    → Add stub results so the API doesn't error
 *
 * @param messages Messages after compaction
 * @returns Sanitized messages with no orphaned pairs
 */
export function sanitizeToolPairs(
  messages: SessionMessage[]
): SessionMessage[] {
  // Build set of surviving tool call IDs (from assistant messages)
  const survivingCallIds = new Set<string>();
  for (const msg of messages) {
    if (msg.role === "assistant") {
      for (const id of getToolCallIds(msg)) {
        survivingCallIds.add(id);
      }
    }
  }

  // Build set of tool result IDs
  const resultCallIds = new Set<string>();
  for (const msg of messages) {
    for (const part of msg.parts) {
      if (
        (part.type.startsWith("tool-") || part.type === "dynamic-tool") &&
        "toolCallId" in part &&
        "output" in part
      ) {
        resultCallIds.add((part as { toolCallId: string }).toolCallId);
      }
    }
  }

  // Remove orphaned results (results whose calls were dropped)
  const orphanedResults = new Set<string>();
  for (const id of resultCallIds) {
    if (!survivingCallIds.has(id)) {
      orphanedResults.add(id);
    }
  }

  let result = messages;
  if (orphanedResults.size > 0) {
    result = result.map((msg) => {
      const filteredParts = msg.parts.filter((part) => {
        if (
          (part.type.startsWith("tool-") || part.type === "dynamic-tool") &&
          "toolCallId" in part &&
          "output" in part
        ) {
          return !orphanedResults.has(
            (part as { toolCallId: string }).toolCallId
          );
        }
        return true;
      });
      if (filteredParts.length !== msg.parts.length) {
        return { ...msg, parts: filteredParts } as SessionMessage;
      }
      return msg;
    });
  }

  // Add stub results for calls whose results were dropped
  const missingResults = new Set<string>();
  for (const id of survivingCallIds) {
    if (!resultCallIds.has(id) && !orphanedResults.has(id)) {
      missingResults.add(id);
    }
  }

  if (missingResults.size > 0) {
    const patched: SessionMessage[] = [];
    for (const msg of result) {
      patched.push(msg);
      if (msg.role === "assistant") {
        for (const id of getToolCallIds(msg)) {
          if (missingResults.has(id)) {
            // Find the tool name from the call
            const callPart = msg.parts.find(
              (p) =>
                "toolCallId" in p &&
                (p as { toolCallId: string }).toolCallId === id
            ) as { toolName?: string } | undefined;

            patched.push({
              id: `stub-${id}`,
              role: "assistant",
              parts: [
                {
                  type: "tool-result" as const,
                  toolCallId: id,
                  toolName: callPart?.toolName ?? "unknown",
                  result:
                    "[Result from earlier conversation — see context summary above]"
                } as unknown as SessionMessage["parts"][number]
              ],
              createdAt: new Date()
            } as SessionMessage);
          }
        }
      }
    }
    result = patched;
  }

  // Remove empty messages (all parts filtered out)
  return result.filter((msg) => msg.parts.length > 0);
}

// ── Summary Budget ───────────────────────────────────────────────────

/**
 * Compute a summary token budget based on the content being compressed.
 * 20% of the compressed content, clamped to 2K-8K tokens.
 */
export function computeSummaryBudget(messages: SessionMessage[]): number {
  const contentTokens = estimateMessageTokens(messages);
  // Summary is ~20% of the content being compressed.
  // The summary replaces the compressed middle, so it's sized relative
  // to what it's replacing — not the tail budget (they occupy different
  // slots in the context window).
  const budget = Math.floor(contentTokens * 0.2);
  return Math.max(100, budget);
}

// ── Structured Summary Prompt ────────────────────────────────────────

/**
 * Build a prompt for LLM summarization of compressed messages.
 *
 * @param messages Messages to summarize
 * @param previousSummary Previous summary for iterative updates (or null for first compaction)
 * @param budget Target token count for the summary
 */
export function buildSummaryPrompt(
  messages: SessionMessage[],
  previousSummary: string | null,
  budget: number
): string {
  const content = messages
    .map((msg) => {
      const textParts = msg.parts
        .filter((p) => p.type === "text")
        .map((p) => (p as { text: string }).text)
        .join("\n");

      const toolParts = msg.parts
        .filter((p) => p.type.startsWith("tool-") || p.type === "dynamic-tool")
        .map((p) => {
          const tp = p as {
            toolName?: string;
            input?: unknown;
            output?: unknown;
          };
          const parts = [`[Tool: ${tp.toolName ?? "unknown"}]`];
          if (tp.input)
            parts.push(`Input: ${JSON.stringify(tp.input).slice(0, 500)}`);
          if (tp.output)
            parts.push(`Output: ${String(tp.output).slice(0, 500)}`);
          return parts.join("\n");
        })
        .join("\n");

      return `[${msg.role}]\n${textParts}${toolParts ? "\n" + toolParts : ""}`;
    })
    .join("\n\n---\n\n");

  if (previousSummary) {
    return `You are updating a conversation summary. A previous summary exists below. New conversation turns have occurred since then and need to be incorporated.

PREVIOUS SUMMARY:
${previousSummary}

NEW TURNS TO INCORPORATE:
${content}

Update the summary. PRESERVE existing information that is still relevant. ADD new information. Remove information only if it is clearly obsolete.

## Topic
[What the conversation is about]

## Key Points
[Important information, decisions, and conclusions from the conversation]

## Current State
[Where things stand now — what has been done, what is in progress]

## Open Items
[Unresolved questions, pending tasks, or next steps discussed]

Target ~${budget} tokens. Be factual — only include information that was explicitly discussed in the conversation. Do NOT invent file paths, commands, or details that were not mentioned. Write only the summary body.`;
  }

  return `Create a concise summary of this conversation that preserves the important information for future context.

CONVERSATION TO SUMMARIZE:
${content}

Use this structure:

## Topic
[What the conversation is about]

## Key Points
[Important information, decisions, and conclusions from the conversation]

## Current State
[Where things stand now — what has been done, what is in progress]

## Open Items
[Unresolved questions, pending tasks, or next steps discussed]

Target ~${budget} tokens. Be factual — only include information that was explicitly discussed in the conversation. Do NOT invent file paths, commands, or details that were not mentioned. Write only the summary body.`;
}

// ── Reference Compaction Implementation ──────────────────────────────

/**
 * Result of a compaction function — describes the overlay to store.
 */
export interface CompactResult {
  /** First message ID in the compacted range */
  fromMessageId: string;
  /** Last message ID in the compacted range */
  toMessageId: string;
  /** Summary text to store as the overlay */
  summary: string;
}

export interface CompactOptions {
  /**
   * Function to call the LLM for summarization.
   * Takes a user prompt string, returns the LLM's text response.
   */
  summarize: (prompt: string) => Promise<string>;

  /** Number of head messages to protect (default: 2) */
  protectHead?: number;

  /** Token budget for tail protection (default: 20000) */
  tailTokenBudget?: number;

  /** Minimum tail messages to protect (default: 2) */
  minTailMessages?: number;

  /**
   * Optional counter for tail-budget decisions. Use this when a tokenizer or
   * model-reported accounting is available; otherwise the Workers-safe
   * heuristic is used.
   */
  tokenCounter?: CompactTokenCounter;
}

/**
 * Reference compaction implementation.
 *
 * Implements the full hermes-style compaction algorithm:
 * 1. Protect head messages (first N)
 * 2. Protect tail by token budget (walk backward)
 * 3. Align boundaries to tool call groups
 * 4. Summarize middle section with LLM (structured format)
 * 5. Sanitize orphaned tool pairs
 * 6. Iterative summary updates on subsequent compactions
 *
 * @example
 * ```typescript
 * import { createCompactFunction } from "agents/experimental/memory/utils";
 *
 * const session = new Session(provider, {
 *   compaction: {
 *     tokenThreshold: 100000,
 *     fn: createCompactFunction({
 *       summarize: (prompt) => generateText({ model, prompt }).then(r => r.text)
 *     })
 *   }
 * });
 * ```
 */
export function createCompactFunction(opts: CompactOptions) {
  const protectHead = opts.protectHead ?? 3;
  const tailTokenBudget = opts.tailTokenBudget ?? 20000;
  const minTailMessages = opts.minTailMessages ?? 2;

  return async (
    messages: SessionMessage[],
    context?: CompactContext
  ): Promise<CompactResult | null> => {
    if (messages.length <= protectHead + minTailMessages) {
      return null;
    }

    // Prefer an explicit counter; otherwise adapt the Session's counter (flowed
    // via CompactContext) so a single `tokenCounter` on `compactAfter` drives
    // the boundary cut too — without it, a fire counter + the default heuristic
    // under-counting a tool-heavy history makes compaction fire every turn but
    // never shorten anything. The session counter is whole-prompt shaped; for
    // the tail walk we feed it individual messages with empty system/context.
    //
    // Caveat: this counter is invoked once PER MESSAGE. A tokenizer-style
    // counter yields accurate per-message tokens; a counter that returns a
    // fixed whole-prompt total (e.g. `usage.inputTokens`) returns the same
    // value for every message, which degrades `tailTokenBudget` to
    // `minTailMessages` — compaction still runs and context stays bounded, but
    // the byte budget is effectively ignored. It is also called O(n) times per
    // compaction, so an async/remote counter (e.g. a `count_tokens` API) will
    // be slow. For precise tail budgeting with such counters, pass an explicit
    // per-message `CompactOptions.tokenCounter` instead.
    const sessionCounter = context?.tokenCounter;
    const tailCounter: CompactTokenCounter | undefined =
      opts.tokenCounter ??
      (sessionCounter
        ? (msgs) =>
            sessionCounter({
              messages: msgs,
              systemPrompt: "",
              contextBlocks: []
            })
        : undefined);

    // 1. Find compression boundaries
    let compressStart = protectHead;
    compressStart = alignBoundaryForward(messages, compressStart);

    let compressEnd = tailCounter
      ? await findTailCutByTokensWithCounter(
          messages,
          compressStart,
          tailCounter,
          tailTokenBudget,
          minTailMessages
        )
      : findTailCutByTokens(
          messages,
          compressStart,
          tailTokenBudget,
          minTailMessages
        );

    if (compressEnd <= compressStart) {
      return null;
    }

    // Filter out compaction overlay messages — they have virtual IDs
    // and should not be included in the summary prompt or used as range IDs
    const middleMessages = messages
      .slice(compressStart, compressEnd)
      .filter((m) => !isCompactionMessage(m));

    if (middleMessages.length === 0) return null;

    // 2. Generate summary — extract previous summary from compaction overlays
    const existingCompaction = messages.find(isCompactionMessage);
    const previousSummary = existingCompaction
      ? existingCompaction.parts
          .filter((p) => p.type === "text")
          .map((p) => (p as { text: string }).text)
          .join("\n")
      : null;

    const budget = computeSummaryBudget(middleMessages);
    const prompt = buildSummaryPrompt(middleMessages, previousSummary, budget);
    const summary = await opts.summarize(prompt);

    if (!summary.trim()) return null;

    return {
      fromMessageId: middleMessages[0].id,
      toMessageId: middleMessages[middleMessages.length - 1].id,
      summary
    };
  };
}
